import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createMediaUploadUrl,
  createPost,
  uploadFileToSignedUrl,
} from '../../services/communityService';

const getMediaType = (file) => {
  if (!file?.type) return 'none';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('image/')) return 'image';
  return 'none';
};

const CommunityPostComposer = ({
  allowedChannels = ['talent_pool', 'workforce'],
  channelOptions = [],
  currentUser,
  onPosted,
  onCancel,
}) => {
  useUiLanguage();
  const { t } = useTranslation('community');
  const [text, setText] = useState('');
  const [selectedTargets, setSelectedTargets] = useState(() =>
    allowedChannels.length > 0 ? [allowedChannels[0]] : []
  );
  const [mediaFile, setMediaFile] = useState(null);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');
  const availableOptions = useMemo(() => {
    const source = Array.isArray(channelOptions) && channelOptions.length > 0
      ? channelOptions
      : allowedChannels.map((channel) => ({
          value: channel,
          label: channel.replace(/[_-]+/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase())
        }));

    return source.filter((option) => allowedChannels.includes(option.value));
  }, [allowedChannels, channelOptions]);

  const selectedMediaType = useMemo(() => getMediaType(mediaFile), [mediaFile]);
  const textCount = text.length;

  useEffect(() => {
    setSelectedTargets((prev) => {
      const filtered = prev.filter((channel) => allowedChannels.includes(channel));
      if (filtered.length > 0) {
        return filtered;
      }
      return allowedChannels.length > 0 ? [allowedChannels[0]] : [];
    });
  }, [allowedChannels]);

  const toggleTarget = (channel) => {
    if (!allowedChannels.includes(channel)) return;
    setSelectedTargets((prev) => (
      prev.includes(channel)
        ? prev.filter((entry) => entry !== channel)
        : [...prev, channel]
    ));
  };

  const onFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setMediaFile(null);
      return;
    }

    const mediaType = getMediaType(file);
    if (mediaType === 'none') {
      setError(t('admin.errors.unsupportedMedia', 'Only image and video files are supported.'));
      setMediaFile(null);
      return;
    }

    setError('');
    setMediaFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (selectedTargets.length === 0) {
      setError(t('admin.errors.selectChannel'));
      return;
    }

    const trimmedText = text.trim();
    if (!trimmedText && !mediaFile) {
      setError(t('admin.errors.emptyPost'));
      return;
    }

    try {
      setPosting(true);
      setError('');

      let mediaStoragePath = null;
      let mediaMimeType = null;
      let mediaType = 'none';

      if (mediaFile) {
        mediaType = selectedMediaType;
        mediaMimeType = mediaFile.type;
        const draftPostId = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        const uploadResult = await createMediaUploadUrl(currentUser, {
          postId: draftPostId,
          mediaType,
          mimeType: mediaMimeType,
        });

        await uploadFileToSignedUrl({
          uploadUrl: uploadResult.upload_url,
          file: mediaFile,
          mimeType: mediaMimeType,
        });

        mediaStoragePath = uploadResult.storage_path;
      }

      await createPost(currentUser, {
        text: trimmedText,
        targets: selectedTargets.reduce((acc, channel) => {
          acc[channel] = true;
          return acc;
        }, {}),
        media_type: mediaType,
        media_storage_path: mediaStoragePath,
        media_mime_type: mediaMimeType,
      });

      setText('');
      setMediaFile(null);
      setSelectedTargets(allowedChannels.length > 0 ? [allowedChannels[0]] : []);
      if (onPosted) onPosted();
    } catch (err) {
      console.error('Community post publish failed:', err);
      setError(err.message || t('admin.errors.postFailed'));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="community-composer-panel">
      <div className="community-composer-header">
        <div>
          <h3 className="community-card-title" style={{ marginBottom: 4 }}>{t('admin.composerTitle')}</h3>
          <p className="community-muted" style={{ fontSize: 13 }}>
            {t('admin.composerHelper', 'Share concise updates with one or both private communities.')}
          </p>
        </div>
        <span className="community-composer-count">{textCount}/5000</span>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="community-composer-field">
          <div className="community-section-title">{t('admin.messageTitle', 'Message')}</div>
          <textarea
            className="community-composer-text"
            value={text}
            maxLength={5000}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('admin.textPlaceholder')}
          />
        </div>

        <div className="community-section-title">{t('admin.targetsTitle', 'Publish to')}</div>
        <div className="community-channel-row community-channel-toggle-row">
          {availableOptions.map((channel) => {
            const selected = selectedTargets.includes(channel.value);
            return (
              <button
                key={channel.value}
                type="button"
                className={`community-channel-toggle ${selected ? 'selected' : ''}`}
                onClick={() => toggleTarget(channel.value)}
                aria-pressed={selected}
              >
                <i className={`fa-solid ${selected ? 'fa-check-circle' : 'fa-circle'}`} />
                <span>{uiText(channel.label)}</span>
              </button>
            );
          })}
        </div>

        <div className="community-section-title">{t('admin.mediaTitle', 'Attachment (optional)')}</div>
        <div className="community-composer-actions">
          <div>
            <label className="community-attach-label">
              <i className="fa-solid fa-paperclip" aria-hidden="true" />
              <span>{t('admin.media.attach')}</span>
              <input
                type="file"
                onChange={onFileChange}
                accept="image/*,video/*"
                style={{ display: 'none' }}
              />
            </label>
            {mediaFile && (
              <div className="community-attach-name community-upload-pill">
                <i className={`fa-solid ${selectedMediaType === 'video' ? 'fa-video' : 'fa-image'}`} />
                <span>{mediaFile.name}</span>
                <button
                  type="button"
                  className="community-btn secondary"
                  style={{ marginInlineStart: 8, padding: '3px 8px' }}
                  onClick={() => setMediaFile(null)}
                >
                  {t('admin.media.remove')}
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {onCancel && (
              <button type="button" className="community-btn secondary" onClick={onCancel}>
                {t('admin.cancel', 'Cancel')}
              </button>
            )}
            <button type="submit" className="community-btn" disabled={posting}>
              {posting ? t('admin.posting') : t('admin.post')}
            </button>
          </div>
        </div>

        {error && <div className="community-message error">{uiText(error)}</div>}
      </form>
    </div>
  );
};

export default CommunityPostComposer;
